'use client';

import React from 'react';
import type {
	FormatParts,
	FormatResult,
	ValueSign,
} from '@velocity-exchange/common/format';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { cm } from 'src/utils/classNames';
import type { WithClassName } from 'src/utils/UtilityTypes';
import useNumberDisplay, {
	type NumberDisplayFormat,
} from 'src/components/NumberDisplay/hooks/useNumberDisplay';

/**
 * Read from the untouched input, so a small negative that renders as `-$0.00`
 * still colours negative and only an exact zero dims.
 */
const SIGN_COLORS: Record<ValueSign, string> = {
	positive: 'text-text-positive-green-button',
	negative: 'text-text-negative-red-button',
	zero: 'text-text-default',
	none: '',
};

/** The one glyph a masked account value renders as. */
export const HIDDEN_ACCOUNT_VALUE_TEXT = '*****';

export interface NumberDisplayRenderProps extends WithClassName {
	/**
	 * Shows `*****` when `true`.
	 */
	isAccountValueToHide?: boolean;
	/**
	 * Shows a skeleton placeholder when `true`.
	 *
	 * You should also use `skeletonClassName` to apply width / height.
	 */
	loading?: boolean;
	skeletonClassName?: string;
	/**
	 * Applies color to the value based on its sign (i.e. - / +) when `true`.
	 */
	signDependentColor?: boolean;
	/**
	 * Override the formatted value with a custom React node.
	 *
	 * Note, this takes precedence over `fallback`.
	 */
	displayOverride?: React.ReactNode;
	/**
	 * Applies `opacity-50` to the value when `true`.
	 */
	dimIfZero?: boolean;
	/**
	 * Renders the formatted parts instead of the plain text.
	 */
	renderParts?: (parts: FormatParts, result: FormatResult) => React.ReactNode;
	/**
	 * Element the value renders as. Defaults to `span`.
	 *
	 * Has no effect on the loading skeleton, which always renders its own
	 * element regardless of `as`.
	 */
	as?: React.ElementType;
}

export type NumberDisplayProps = NumberDisplayRenderProps & NumberDisplayFormat;

export default function NumberDisplay(props: NumberDisplayProps) {
	const {
		signDependentColor,
		isAccountValueToHide,
		dimIfZero,
		loading,
		skeletonClassName,
		displayOverride,
		className,
		renderParts,
		as,
	} = props;

	const { result, showAccountValues } = useNumberDisplay(props);

	/*
	 * Render precedence, highest first:
	 * 1. loading
	 * 2. isAccountValueToHide
	 * 3. displayOverride
	 * 4. sentinel
	 * 5. a non-ok status: the caller's `fallback` for a nullish value, the
	 *    layer's own text otherwise (e.g. `?` for an invalid digit spec)
	 * 6. renderParts
	 * 7. text
	 *
	 * The sentinel and status levels exist only to gate renderParts: all three
	 * text levels render result.text.
	 */
	if (loading) {
		return <SkeletonValuePlaceholder className={skeletonClassName} loading />;
	}

	const Component = as ?? 'span';

	if (isAccountValueToHide && !showAccountValues) {
		return (
			<Component className={className}>{HIDDEN_ACCOUNT_VALUE_TEXT}</Component>
		);
	}

	// A sentinel and a non-ok status carry no parts, so they keep their text.
	const hasParts = !result.isSentinel && result.status === 'ok';
	const displayValue =
		displayOverride ??
		(hasParts && renderParts ? renderParts(result.parts, result) : result.text);

	return (
		<Component
			className={cm(
				'whitespace-nowrap',
				dimIfZero && result.exactSign === 'zero' && 'opacity-50',
				signDependentColor && SIGN_COLORS[result.exactSign],
				className
			)}
		>
			{displayValue}
		</Component>
	);
}
